import { ScheduleBlockDto } from "@aika/types";
import axios from "axios";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSettings } from "Settings";
import { AikaDate } from "utils/AikaDate";
import { ScheduleBlock } from "./ScheduleBlock";

const copyBlocks = (blocks: ScheduleBlock[]) => blocks.map((x) => x.clone());

export type MoveDirection = "up" | "down";
export type MoveMode = "push" | "normal";

type CalendarContextType = {
  blocks: ScheduleBlock[];
  addBlock: (newBlock: ScheduleBlock) => void;
  deleteBlock: (id: string) => void;
  updateBlock: (id: string, data: Partial<ScheduleBlockDto>) => void;
  moveBlock: (id: string, day: number, start: number) => void;
  resizeBlock: (
    id: string,
    direction: MoveDirection,
    mode: MoveMode,
    startOrLength: number
  ) => void;
  commitAction: () => void;
  commit: () => void;
  rollback: () => void;
};

type MoveCalendarAction = {
  id: string;
  type: "move";
  day: number;
  start: number;
};

type ResizeCalendarAction = {
  id: string;
  type: "resize";
  from: MoveDirection;
  startOrLength: number;
  mode: MoveMode;
};

type CalendarAction = MoveCalendarAction | ResizeCalendarAction;

const resolveResize = (
  blocks: ScheduleBlock[],
  action: ResizeCalendarAction,
  startOfDay: number,
  endOfDay: number
): ScheduleBlock[] => {
  const items = blocks.map((x) => x.clone());

  const targetBlock = items.find((x) => x.id === action.id);
  const targetDate = targetBlock.date;

  if (action.from === "up") {
    const end = targetBlock.end;
    targetBlock.start = Math.max(startOfDay, action.startOrLength);
    targetBlock.end = end;
  } else {
    const maxLength = endOfDay - targetBlock.start;
    targetBlock.length = Math.min(maxLength, action.startOrLength);
  }

  const { start, end } = targetBlock;

  const potentialOverlaps = items.filter(
    (x) => x.id !== action.id && x.isOnDay(targetDate)
  );

  // remove
  potentialOverlaps
    .filter((x) => x.start >= start && x.start + x.length <= end)
    .forEach((x) => {
      x.isDeleted = true;
    });

  // move starts
  potentialOverlaps
    .filter((x) => x.start >= start && x.start < end && x.end > end)
    .forEach((x) => {
      let e = x.start + x.length;
      x.start = end;
      x.length = e - end;
    });

  // move lengths
  potentialOverlaps
    .filter((x) => x.start < start && x.end > start)
    .forEach((x) => {
      x.length = start - x.start;
    });

  return items;
};

const resolveMove = (
  blocks: ScheduleBlock[],
  action: MoveCalendarAction,
  startDate: AikaDate
): ScheduleBlock[] => {
  const copy = blocks.map((x) => x.clone());

  const targetDate = startDate.addDays(action.day);

  const targetBlock = copy.find((x) => x.id === action.id);

  targetBlock.start = action.start;
  targetBlock.date = targetDate;

  return copy.map<ScheduleBlock | null>((x) => {
    if (x.id !== action.id && x.isOnDay(targetDate)) {
      if (targetBlock.covers(x)) {
        x.isDeleted = true;
      } else if (x.start < targetBlock.start && x.end > targetBlock.start) {
        x.end = targetBlock.start;
      } else if (x.start < targetBlock.end) {
        x.start = targetBlock.end;
      }
    }
    return x;
  });
};

export function useCalendar(
  startDate: AikaDate,
  dayCount: number
): CalendarContextType {
  const [lastCommit, setLastCommit] = useState<ScheduleBlock[]>();
  const [blocks, setBlocks] = useState<ScheduleBlock[]>([]);
  const [action, setAction] = useState<CalendarAction | null>(null);
  const [settings] = useSettings();

  const { startOfDay, endOfDay } = settings;

  useEffect(() => {
    axios
      .get<ScheduleBlockDto[]>(
        `/api/calendar?start=${startDate.toString()}&count=${dayCount}`
      )
      .then(({ data }) => {
        const blocks = data.map((x) => ScheduleBlock.fromDto(x));
        setBlocks(blocks);
        setLastCommit(copyBlocks(blocks));
      });
  }, [startDate, dayCount]);

  const resolveAction = useCallback(
    (blocks: ScheduleBlock[], action: CalendarAction) => {
      if (action?.type === "move") {
        return resolveMove(blocks, action, startDate);
      }

      if (action?.type === "resize") {
        return resolveResize(blocks, action, startOfDay, endOfDay);
      }

      return blocks.map((x) => x.clone());
    },
    [startDate, startOfDay, endOfDay]
  );

  const activeBlocks = useMemo<ScheduleBlock[]>(() => {
    return resolveAction(blocks, action).filter((x) => !x.isDeleted);
  }, [action, blocks, resolveAction]);

  const commitAction = useCallback(() => {
    const newBlocks = resolveAction(blocks, action);
    setBlocks(newBlocks);
    setAction(null);
  }, [blocks, action, resolveAction]);

  const commit = useCallback(async () => {
    const spec = blocks.map((x) => x.asDto());
    await axios.post("/api/blocks/sync", spec);
    setLastCommit(copyBlocks(blocks).filter((x) => !x.isDeleted));
  }, [blocks]);

  const addBlock = useCallback(
    (newBlock: ScheduleBlock) => {
      setBlocks((blocks) => [...blocks, newBlock]);
      commit();
    },
    [commit]
  );

  const updateBlock = useCallback(
    (id: string, data: Partial<ScheduleBlockDto>) => {
      setBlocks((old) =>
        old.map((x) => {
          if (x.id === id) {
            x.description = data.description;
            x.color = data.color;
            x.tags = data.tags;
          }

          return x;
        })
      );
    },
    []
  );

  const deleteBlock = useCallback((id: string) => {
    setBlocks((old) => old.filter((x) => x.id !== id));
    axios.delete(`/api/blocks/${id}`);
  }, []);

  const resizeBlock = useCallback(
    (
      id: string,
      direction: MoveDirection,
      mode: MoveMode,
      startOrLength: number
    ) => {
      setAction((old) => {
        const action = {
          id: id,
          type: "resize",
          from: direction,
          startOrLength: startOrLength,
          mode: mode,
        } as const;

        if (isEqual(old, action)) {
          return old;
        }
        return action;
      });
    },
    []
  );

  const moveBlock = useCallback((id: string, day: number, start: number) => {
    setAction({
      id: id,
      day: day,
      type: "move",
      start: start,
    });
  }, []);

  const rollback = useCallback(() => {
    setBlocks(lastCommit);
  }, [lastCommit]);

  const value = {
    blocks: activeBlocks,
    addBlock,
    deleteBlock,
    commit,
    rollback,
    resizeBlock,
    moveBlock,
    commitAction,
    updateBlock,
  };

  return value;
}
